import { Formik } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState } from 'react';
import { BasisTheoryProvider, useBasisTheory } from '@basis-theory/react-elements';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { add, subDays } from 'date-fns';
import { paymentMethod as PaymentMethodTypes } from '@ourbranch/lookups';
import { getPrimaryFarmMortgage, isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import config from 'aws-exports';
import { hydrateFarmPartyForForm } from 'common/helpers/farm-interested-party-helpers';
import { useBasisTheoryElementRefsProvider } from 'common/hooks/basis-theory-elements.context';
import { Loading } from 'core';
import { AuthContext } from 'core/components/auth';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import PriceDetails from './price-details';
import PaymentDetails from './payment-details';
import PaymentMethods from './payment-methods';
import { BillingDetailsForm } from './billing-details-form';
import { getNormalizedBillingDefaults } from './billing-details-form/helpers';
import MismatchBillingDetails from './mismatch-billing-details-modal';
import Footer from '../footer';
import { localToUtcTime } from '../dates';
import { validatePaymentTab } from './payment-tab.validation';
import { brandConfig } from 'brand-config';
import {
  getBillingHoldConfig,
  usesEnhancedBillingHold,
  getEnhancedMaxBillingHoldDate,
  isPlacingBillingHold
} from './billing-hold-config';

const stripePromise = loadStripe(config.stripeKey);

// Escrow billing seeds `defaultEscrowAccount` from the policy's primary mortgage; fall back to an
// empty shape so the escrow fields still render. Ported from the policy-settings container when the
// billing form moved to this tab (#32450).
const getPrimaryMortgage = ({ policy, primaryMortgageForBilling }) => {
  const emptyMortgage = {
    loanNumber: undefined,
    mortgageHolderAddress: undefined,
    mortgageHolderName: undefined,
    mortgageHolderId: undefined,
    mortgageHolderEmail: undefined,
    mortgageHolderFax: undefined
  };

  if (policy?.defaultEscrowAccount?.mortgageHolderName) {
    return policy.defaultEscrowAccount;
  }
  return primaryMortgageForBilling || emptyMortgage;
};

const PaymentTab = observer(({ loadingPreview, handleChangePolicy }) => {
  const session = useContext(AuthContext);
  const toast = useToast();
  const { bt } = useBasisTheory(config.basisTheoryApiKey, {
    elements: true,
    environment: config.basisTheoryElementsEnv
  });
  const { Provider: BasisTheoryElementsProvider, refs: elementRefs } = useBasisTheoryElementRefsProvider();

  const { isTeamLeader, canEdit, viewOnly } = session;
  const {
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();
  const [showMismatchModal, setShowMismatchModal] = useState(false);

  const { policyDetails, billingDetails } = policyStore;
  // Farm mortgages live per-structure on building.mortgageDetails (#32526); the legacy flat
  // farm.mortgageDetails is cleared on save, so resolve the farm primary from the buildings.
  // Home/condo still carry the flat mortgageDetails array.
  const primaryMortgageForBilling = isFarmBasedPolicyType(policyStore.policy?.policyType)
    ? getPrimaryFarmMortgage(policyDetails)
    : (policyDetails?.home || policyDetails?.condo)?.mortgageDetails?.find((mortgage) => mortgage?.primary);
  const primaryMortgage = getPrimaryMortgage({ policy: policyStore.policy, primaryMortgageForBilling });

  const { defaultBankAccount, defaultCreditCard } = useMemo(() => {
    // not all accounts will have a default so set it from activePaymentMethod
    let defaultBankAccount = policyStore.policy.defaultBankAccount;
    let defaultCreditCard = policyStore.policy.defaultCreditCard;
    const activeAccount = billingDetails?.activePaymentMethod;

    const isBankAccount = (acct) => acct?.type === 'us_bank_account' || acct?.id?.startsWith('ba') || !!acct?.bankName;
    const isCreditCard = (acct) =>
      acct?.type === 'card' || acct?.id?.startsWith('card') || (!!acct?.brand && !acct?.bankName);

    const activeBankAccount = isBankAccount(activeAccount)
      ? activeAccount
      : billingDetails?.allPaymentMethods?.find(isBankAccount);
    const activeCreditCard = isCreditCard(activeAccount)
      ? activeAccount
      : billingDetails?.allPaymentMethods?.find(isCreditCard);

    // Stripe and the policy can drift (e.g. someone updated the customer directly in Stripe).
    // Only override when Stripe has a concrete value that disagrees with the policy — never clobber
    // a valid policy.defaultX with `{ id: undefined }` just because Stripe returned nothing
    // (which also happens during stale-cache windows right after a confirm). See #29196.
    if (activeBankAccount?.id && activeBankAccount.id !== policyStore.policy.defaultBankAccount?.id) {
      defaultBankAccount = activeBankAccount;
    }
    if (activeCreditCard?.id && activeCreditCard.id !== policyStore.policy.defaultCreditCard?.id) {
      defaultCreditCard = activeCreditCard;
    }
    return { defaultBankAccount, defaultCreditCard };
  }, [policyStore.policy, billingDetails]);

  // Seed paymentType/paymentMode with the same values the payment-method sub-components force on mount,
  // so the form doesn't open pre-dirtied (spurious "Save changes" footer). See getNormalizedBillingDefaults.
  const normalizedBillingDefaults = getNormalizedBillingDefaults({
    paymentMethod: policyStore.policy.paymentMethod,
    paymentType: policyStore.policy.paymentType,
    paymentMode: policyStore.policy.paymentMode,
    policyType: policyStore.policy.policyType
  });

  const onSubmit = (values) => {
    const parsed = {
      ...policyStore?.policy,
      fees: values.fees,
      billingAddInstallments: values.billingAddInstallments,
      billingHoldUntil: values.billingHoldUntil,
      // Place-on-Hold brand fields (#33568); placedBy/placedAt are stamped server-side, not sent here.
      billingHoldReason: values.billingHoldReason,
      billingHoldBlocksCancellations: values.billingHoldBlocksCancellations,
      skipImmediateBillOrRefund: values.skipImmediateBillOrRefund,
      paymentMethod: values.paymentMethod,
      paymentType: values.paymentType,
      billingDayOfMonth: values.billingDayOfMonth,
      paymentMode: values.paymentMode,
      directBillPreferredPaymentMethod: values.directBillPreferredPaymentMethod,
      stripeCustomerId: values.stripeCustomerId,
      otherStripeCustomers: values.otherStripeCustomers,
      renewalPaymentMethod: values.renewalPaymentMethod,
      renewalPaymentType: values.renewalPaymentType,
      defaultEscrowAccount: values.defaultEscrowAccount,
      defaultBankAccount: values.defaultBankAccount,
      defaultCreditCard: values.defaultCreditCard
    };
    // The form seeds defaultEscrowAccount once at mount, so it can be empty even when the policy has
    // a primary mortgage (validation accepts that case); backfill it here so the saved policy always
    // carries the escrow account.
    if (
      parsed.paymentMethod === PaymentMethodTypes.Escrow &&
      !parsed.defaultEscrowAccount?.mortgageHolderName &&
      primaryMortgage?.mortgageHolderName
    ) {
      parsed.defaultEscrowAccount = primaryMortgage;
    }
    handleChangePolicy(parsed);
    policyStore.setChanged(false);
  };

  if (policyStore.loading) {
    return <Loading type="secondary" />;
  }
  const sevenDaysBeforeEndDate = subDays(new Date(policyStore.policy.endDate), 7);

  const determineInitialMaxBillingHoldDate = () => {
    // billing holds should only last up to 7 days after billingDayOfMonth
    const billingDayOfMonth = policyStore?.policy?.billingDayOfMonth;

    // start with the billingDayOfMonth without adding anything yet
    const startBillingHoldDate = localToUtcTime(
      new Date().setDate(billingDayOfMonth),
      policyStore?.geographicState
    ).setHours(0, 0, 0, 0);
    let maxBillingHoldDate;

    // need to add a month and a week if today is after billing date
    if (new Date().getDate() > billingDayOfMonth) {
      maxBillingHoldDate = add(startBillingHoldDate, { weeks: 1, months: 1 });
    } else {
      // otherwise, just add a week
      maxBillingHoldDate = add(startBillingHoldDate, { weeks: 1 });
    }

    return maxBillingHoldDate < sevenDaysBeforeEndDate ? maxBillingHoldDate : sevenDaysBeforeEndDate;
  };

  // Place-on-Hold (#33568): mutual brands cap the hold at today + brand maxDays (15; teamLeaderMaxDays,
  // 30, for a team leader, #37845) and require a reason. Branch keeps its existing hold-date logic
  // untouched (usesEnhancedBillingHold === false).
  const billingHoldConfig = getBillingHoldConfig(brandConfig);
  const getMaxBillingHoldDate = () => {
    if (usesEnhancedBillingHold(billingHoldConfig)) {
      return getEnhancedMaxBillingHoldDate({
        config: billingHoldConfig,
        state: policyStore?.geographicState,
        sevenDaysBeforeEndDate,
        isTeamLeader
      });
    }
    return isTeamLeader ? sevenDaysBeforeEndDate : determineInitialMaxBillingHoldDate();
  };
  const maxBillingHoldDate = getMaxBillingHoldDate();

  const validate = async (values) => {
    // Require a reason only when the staffer is actively placing/changing a hold in THIS edit — not
    // merely because an active hold already exists (else a pre-existing reasonless hold would block
    // saving unrelated Payment-tab fields). See isPlacingBillingHold.
    const placingBillingHold = isPlacingBillingHold({ values, savedPolicy: policyStore.policy });
    const errors = await validatePaymentTab(values, {
      ...values,
      sevenDaysBeforeEndDate,
      isTeamLeader,
      geographicState: policyStore?.geographicState,
      policyDetails,
      billingDetails,
      primaryMortgage,
      billingHoldConfig,
      placingBillingHold,
      savedBillingHoldUntil: policyStore.policy?.billingHoldUntil,
      // A cancelled / in-cancellation policy skips the "Escrow requires a mortgage holder name"
      // requirement (ported from the Settings form's `cancel` bypass; cancellation can't be
      // initiated from this tab, so the equivalent trigger here is the policy already cancelling).
      cancel: policyStore.isCancelledOrInCancellation
    });
    if (errors.validationFailedToRun) {
      toast.notify({ type: 'error', message: errors.validationFailedToRun });
    }
    return errors;
  };

  return (
    <BasisTheoryProvider bt={bt}>
      <BasisTheoryElementsProvider value={elementRefs}>
        <Elements stripe={stripePromise}>
          <Formik
            initialValues={{
              billingAddInstallments: 0,
              billingHoldUntil: policyStore.policy ? policyStore.policy.billingHoldUntil : undefined,
              billingHoldReason: policyStore.policy?.billingHoldReason ?? '',
              billingHoldBlocksCancellations: policyStore.policy?.billingHoldBlocksCancellations ?? false,
              fees: policyStore.policy.fees,
              maxBillingHoldDate,
              memberPaidEarly: false,
              paymentMethod: policyStore.policy.paymentMethod,
              paymentType: normalizedBillingDefaults.paymentType,
              billingDayOfMonth: policyStore.policy.billingDayOfMonth,
              paymentMode: normalizedBillingDefaults.paymentMode,
              directBillPreferredPaymentMethod: policyStore.policy.directBillPreferredPaymentMethod,
              stripeCustomerId: policyStore.policy.stripeCustomerId,
              otherStripeCustomers: policyStore.policy.otherStripeCustomers,
              // farmInterestedParties is hydrated for the Bill-to-other display; it is not edited on
              // this tab, so the raw policy value passes through untouched on submit.
              farmInterestedParties: (policyStore.policy.farmInterestedParties || []).map(hydrateFarmPartyForForm),
              renewalPaymentMethod: policyStore.policy.renewalPaymentMethod ?? policyStore.policy.paymentMethod,
              renewalPaymentType: policyStore.policy.renewalPaymentType,
              defaultEscrowAccount: primaryMortgage,
              defaultBankAccount,
              defaultCreditCard
            }}
            validate={validate}
            onSubmit={onSubmit}
          >
            {({ values, touched, handleSubmit }) => {
              // The footer keys off the shared `policyStore.changed` flag: fee/hold edits set it
              // imperatively, and BillingDetailsForm mirrors Formik `dirty` onto it via an effect.
              const showFooter = canEdit && !viewOnly && policyStore.changed;

              // When the primary and renewal payment method/frequency diverge, confirm intent before
              // saving (mirrors the policy-settings flow the form came from).
              const onFooterClick = () => {
                if (
                  (values.paymentMethod !== values.renewalPaymentMethod ||
                    values.paymentType !== values.renewalPaymentType) &&
                  (Object.keys(touched).includes('paymentMethod') || Object.keys(touched).includes('paymentType'))
                ) {
                  setShowMismatchModal(true);
                  return;
                }
                handleSubmit();
              };

              return (
                <>
                  <MismatchBillingDetails
                    open={showMismatchModal}
                    onSubmit={handleSubmit}
                    onClose={() => setShowMismatchModal(false)}
                  />
                  <PriceDetails />
                  <BillingDetailsForm />
                  <PaymentDetails />
                  {policyStore.policy.stripeCustomerId && <PaymentMethods />}
                  {showFooter && (
                    <Footer title="Save changes" loadingPreview={loadingPreview} onClick={onFooterClick} />
                  )}
                </>
              );
            }}
          </Formik>
        </Elements>
      </BasisTheoryElementsProvider>
    </BasisTheoryProvider>
  );
});

PaymentTab.propTypes = {
  loadingPreview: PropTypes.bool.isRequired,
  handleChangePolicy: PropTypes.func.isRequired
};

export default PaymentTab;
